import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  Image,
  Flex,
  Badge,
  VStack,
  HStack,
  Text,
  Heading,
  SimpleGrid,
} from '@chakra-ui/react';

import { getMarketplaceLogo } from '../../../components/marketplaceLogo';
import { useColors } from './colors';

import StoreMapping from './StoreMapping';
import ApiConfiguration from './ApiConfiguration';
import LastestUpdatesGraph from './LastestUpdatesGraph';
import SalesOrderNumberMapping from './SalesOrderNumberMapping';
import ShipStationTaggerSchedule from './ShipStationTaggerSchedule';
import ShipStationTaggerProgress from './ShipStationTaggerProgress';
import IntegrationSection from './IntegrationSection';

import { updateThirdPartyIntegrationApi } from 'features/third-party-integrations/third-party-integrations.api';

const ShipStationCard = ({
  id,
  name,
  altName,
  apiId = '',
  apiSecret = '',
  shipStationStores = [],
  mappedStores = [],
  storeOptions = [],
  getStoresListStatus = 'idle',
  integrationData = null,
}) => {
  const dispatch = useDispatch();
  const { backgroundCardColor, borderColor, textColor } = useColors();
  const logo = getMarketplaceLogo(name);

  const [storesConnected, setStoresConnected] = useState(0);
  const [timelineData, setTimelineData] = useState([]);

  const { updateThirdPartyIntegrationStatus } = useSelector(
    (state) => state.thirdPartyIntegrations,
  );

  useEffect(() => {
    setStoresConnected(
      integrationData.configurations.find((config) => config.key === 'mappedStores')
        ?.value?.length || 0,
    );

    const timelineConfig = integrationData.configurations.find(
      (config) => config.key === 'orderSupplierTaggerUpdateTimeline',
    );
    setTimelineData(timelineConfig?.value || []);
  }, [integrationData]);

  const updateConfigurations = (updatedConfigs) => {
    if (!integrationData || !integrationData.configurations) {
      return updatedConfigs;
    }

    const currentConfigs = [...integrationData.configurations];

    updatedConfigs.forEach((updatedConfig) => {
      const existingIndex = currentConfigs.findIndex(
        (config) => config.key === updatedConfig.key,
      );

      if (existingIndex >= 0) currentConfigs[existingIndex] = updatedConfig;
      else currentConfigs.push(updatedConfig);
    });

    return currentConfigs;
  };

  const handleApiConfigSave = (clientId, clientSecret) => {
    const updatedConfigs = [
      { key: 'clientId', value: clientId },
      { key: 'clientSecret', value: clientSecret },
    ];

    const allConfigurations = updateConfigurations(updatedConfigs);

    return dispatch(
      updateThirdPartyIntegrationApi({
        id,
        name,
        altName,
        configurations: allConfigurations,
      }),
    );
  };

  const handleStoreMappingSave = (updatedMappedStores) => {
    const updatedConfigs = [{ key: 'mappedStores', value: updatedMappedStores }];

    const allConfigurations = updateConfigurations(updatedConfigs);

    return dispatch(
      updateThirdPartyIntegrationApi({
        id,
        name,
        altName,
        configurations: allConfigurations,
      }),
    );
  };

  return (
    <Flex
      direction={{ base: 'column', lg: 'row' }}
      gap={{ base: 6, lg: 8 }}
      alignItems={{ base: 'stretch', lg: 'flex-start' }}
    >
      <Box
        flexShrink={0}
        alignSelf="flex-start"
        display="flex"
        alignItems="flex-start"
        justifyContent={{ base: 'center', lg: 'flex-start' }}
        px={{ base: 0, lg: 2 }}
        pt={{ base: 2, lg: 0 }}
        pb={{ base: 4, lg: 0 }}
        borderBottom={{ base: '1px solid', lg: 'none' }}
        borderRight={{ base: 'none', lg: '1px solid' }}
        borderColor={borderColor}
        pr={{ lg: 6 }}
      >
        <Image
          src={logo}
          alt={`${altName} logo`}
          maxH={{ base: '56px', lg: '72px' }}
          maxW="200px"
          objectFit="contain"
        />
      </Box>

      <Box
        flex={1}
        minW={0}
        bg={backgroundCardColor}
        border="1px solid"
        borderColor={borderColor}
        borderRadius="2xl"
        p={{ base: 5, md: 8 }}
        shadow="sm"
      >
        <Flex
          justify="space-between"
          align="flex-start"
          flexWrap="wrap"
          gap={4}
          mb={8}
          pb={6}
          borderBottom="1px solid"
          borderColor={borderColor}
        >
          <Box>
            <Heading size="md" color={textColor} mb={1}>
              {altName || 'ShipStation'}
            </Heading>
            <Text fontSize="sm" color="gray.500">
              Connect API credentials, map stores, and automate order supplier tagging.
            </Text>
          </Box>
          <Badge
            colorScheme={storesConnected === 0 ? 'red' : 'green'}
            variant="subtle"
            fontSize="sm"
            px={4}
            py={2}
            borderRadius="full"
            textTransform="uppercase"
            letterSpacing="wide"
          >
            {storesConnected} store{storesConnected === 1 ? '' : 's'} connected
          </Badge>
        </Flex>

        <VStack align="stretch" spacing={0}>
          <IntegrationSection
            showDivider={false}
            title="Connection setup"
            description="Configure API access and link ShipStation stores to your marketplace stores."
          >
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <ApiConfiguration
                apiId={apiId}
                apiSecret={apiSecret}
                onSave={handleApiConfigSave}
                updateStatus={updateThirdPartyIntegrationStatus}
              />
              <StoreMapping
                shipStationStores={shipStationStores}
                storeOptions={storeOptions}
                getStoresListStatus={getStoresListStatus}
                mappedStores={mappedStores}
                onSave={handleStoreMappingSave}
                updateStatus={updateThirdPartyIntegrationStatus}
              />
            </SimpleGrid>
          </IntegrationSection>

          <IntegrationSection
            title="Order supplier tagging"
            description="Run manual tagging or review recent activity on the timeline."
            noPadding
          >
            <LastestUpdatesGraph timelineData={timelineData} />
          </IntegrationSection>

          <IntegrationSection
            title="Sales order number mapping"
            description="Upload an Excel file with column A as Order Number and column D as Sales Order #."
          >
            <SalesOrderNumberMapping embedded />
          </IntegrationSection>

          <IntegrationSection
            title="Scheduled tagging"
            description="Run the supplier tagger automatically throughout the day."
          >
            <VStack align="stretch" spacing={6}>
              <ShipStationTaggerSchedule embedded />
              <ShipStationTaggerProgress embedded />
            </VStack>
          </IntegrationSection>
        </VStack>
      </Box>
    </Flex>
  );
};

export default ShipStationCard;
